<template>
    <div
        ref="languageSelector"
        :class="isDesktop ? 'lang-selector-desktop' : 'lang-selector-mobile'"
    >
        <button
            type="button"
            class="lang-selector-btn"
            @click="toggleLanguageDropdown"
            aria-label="Select language"
        >
            <span v-if="!fullDetail" class="lang-globe-icon">
                <!-- SVG Globe Icon -->
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M2 12H22"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2V2Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </span>
            <span
                v-if="fullDetail"
                class="flag"
                :class="currentLanguageFlag"
            ></span>
            <span v-if="fullDetail || isDesktop">{{
                currentLanguageName
            }}</span>
            <span v-else-if="!fullDetail">{{
                currentLanguageCode.toUpperCase()
            }}</span>
        </button>
        <div
            v-if="showLanguageDropdown"
            :class="
                isDesktop ? 'lang-dropdown-desktop' : 'lang-dropdown-mobile'
            "
        >
            <button
                v-for="lang in languages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
                class="lang-dropdown-option"
                :class="{ active: locale === lang.code }"
            >
                <span v-if="fullDetail" class="flag" :class="lang.flag"></span>
                {{ lang.name }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth.store'
import { loadLocaleResources } from '@/i18n.js'
import UserService from '@/services/api/user.service'

// Props
const props = defineProps({
    fullDetail: {
        type: Boolean,
        default: false,
    },
})

const { locale } = useI18n()
const authStore = useAuthStore()
const showLanguageDropdown = ref(false)
const languageSelector = ref(null)
const isChangingLanguage = ref(false)

const languages = [
    { code: 'es', name: 'Español', flag: 'flag flag-es' },
    { code: 'en', name: 'English', flag: 'flag flag-us' },
    { code: 'pt', name: 'Português', flag: 'flag flag-br' },
    { code: 'de', name: 'Deutsch', flag: 'flag flag-de' },
    { code: 'nl', name: 'Nederlands', flag: 'flag flag-nl' },
]

const isDesktop = computed(() => window.innerWidth > 768)

const currentLanguageName = computed(
    () => languages.find((lang) => lang.code === locale.value)?.name || 'English'
)
const currentLanguageCode = computed(
    () => languages.find((lang) => lang.code === locale.value)?.code || 'en'
)
const currentLanguageFlag = computed(
    () =>
        languages.find((lang) => lang.code === locale.value)?.flag ||
        'flag flag-us'
)

const toggleLanguageDropdown = () => {
    showLanguageDropdown.value = !showLanguageDropdown.value
}

const changeLanguage = async (langCode) => {
    if (isChangingLanguage.value || locale.value === langCode) {
        showLanguageDropdown.value = false
        return
    }

    isChangingLanguage.value = true
    showLanguageDropdown.value = false

    try {
        const resolvedLocale = await loadLocaleResources(langCode)
        locale.value = resolvedLocale
        localStorage.setItem('preferredLanguage', resolvedLocale)

        // Save UI language to backend for cross-device sync
        if (authStore.user?.data?._id) {
            try {
                await UserService.updateUser({
                    uiLanguage: resolvedLocale,
                })
                console.log('UI language saved to backend:', resolvedLocale)

                // Update auth store with new uiLanguage
                if (authStore.user?.data) {
                    authStore.user.data.uiLanguage = resolvedLocale
                }
            } catch (backendError) {
                console.warn(
                    'Failed to save UI language to backend:',
                    backendError
                )
                // Continue anyway - localStorage will work for this session
            }
        }
    } catch (error) {
        console.error('Failed to change language:', error)
    } finally {
        isChangingLanguage.value = false
    }
}

const handleClickOutside = (event) => {
    if (
        languageSelector.value &&
        !languageSelector.value.contains(event.target) &&
        showLanguageDropdown.value
    ) {
        showLanguageDropdown.value = false
    }
}

onMounted(async () => {
    document.addEventListener('click', handleClickOutside)
    const savedLanguage = localStorage.getItem('preferredLanguage')
    const validLanguages = languages.map((lang) => lang.code)
    if (savedLanguage && validLanguages.includes(savedLanguage)) {
        if (locale.value !== savedLanguage) {
            try {
                const resolvedLocale = await loadLocaleResources(savedLanguage)
                locale.value = resolvedLocale
            } catch (error) {
                console.error('Failed to initialize saved language:', error)
            }
        }
    } else {
        localStorage.setItem('preferredLanguage', locale.value)
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.lang-selector-mobile {
    background: #0000007a;
    border-radius: 14px;
    padding: 9.5px 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1001;
    backdrop-filter: blur(5px);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.lang-selector-btn {
    background: transparent;
    border: none;
    color: white;
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    padding: 0;
}
.lang-globe-icon {
    display: inline-flex;
    align-items: center;
    margin-right: 5px;
}
.lang-dropdown-mobile {
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 8px 0;
    z-index: 10001;
    min-width: 150px;
    animation: fadeIn 0.2s;
}
.lang-dropdown-option {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 15px;
    border: none;
    background: transparent;
    text-align: left;
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    transition: background 0.2s;
    gap: 8px;
}
.lang-dropdown-option:hover {
    background: #f5f5f5;
}
.lang-dropdown-option.active {
    font-weight: 600;
    color: #6e3dea;
}

.lang-selector-desktop {
    position: relative;
    background: #0000007a;
    border-radius: 14px;
    padding: 13px;
    z-index: 1001;
    backdrop-filter: blur(5px);
    transition: all 0.3s;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.lang-dropdown-desktop {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 8px 0;
    z-index: 10001;
    min-width: 150px;
    animation: fadeIn 0.2s;
}

.lang-selector-desktop .lang-selector-btn {
    font-size: 14px;
    font-weight: 500;
    font-family: 'Basier Circle', sans-serif;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Flag styles */
.lang-selector-btn .flag {
    margin-right: 6px;
    flex-shrink: 0;
}

.lang-dropdown-option .flag {
    flex-shrink: 0;
}
</style>
